// The account's condos. Two copies live here on purpose:
//   - `condos`: every condo (all pages), for the dropdowns that need one
//     (service-team coverage, routing overrides, the resolver, tenant forms);
//   - `page` + `meta`: the server page the Condos list shows.
// Every write refreshes both so a new or archived condo shows up everywhere.
import {
  listCondos,
  getCondo,
  createCondo,
  updateCondo,
  setCondoActive,
  deleteCondo,
} from '../../api/coLiving/condos';
import { listAllPages } from '../../api/coLiving/pagination';

export const state = {
  condos: [],
  page: [],
  meta: { page: 1, pageSize: 10, total: 0 },
  uiFlags: { isFetching: false, isFetchingPage: false },
};

export const getters = {
  getCondos: s => s.condos,
  getActiveCondos: s => s.condos.filter(c => c.active),
  getPage: s => s.page,
  getMeta: s => s.meta,
  getUiFlags: s => s.uiFlags,
};

export const mutations = {
  setCondos(s, condos) {
    s.condos = condos;
  },
  setPage(s, { results, page, page_size: pageSize, total }) {
    s.page = results;
    s.meta = { page, pageSize, total };
  },
  setUiFlag(s, { key, value }) {
    s.uiFlags = { ...s.uiFlags, [key]: value };
  },
};

// Only the latest page request may write the list (see coLivingTickets).
let latestPageRequest = 0;

export const actions = {
  async fetchCondos({ commit }) {
    commit('setUiFlag', { key: 'isFetching', value: true });
    try {
      const condos = await listAllPages(listCondos, { status: 'all' });
      commit('setCondos', condos);
      return condos;
    } finally {
      commit('setUiFlag', { key: 'isFetching', value: false });
    }
  },
  async fetchCondoPage({ commit }, query) {
    latestPageRequest += 1;
    const request = latestPageRequest;
    commit('setUiFlag', { key: 'isFetchingPage', value: true });
    try {
      const { data } = await listCondos(query);
      if (request === latestPageRequest) commit('setPage', data);
      return data;
    } finally {
      if (request === latestPageRequest) {
        commit('setUiFlag', { key: 'isFetchingPage', value: false });
      }
    }
  },
  async fetchCondo(_, id) {
    const { data } = await getCondo(id);
    return data;
  },
  async addCondo({ dispatch }, fields) {
    const { data } = await createCondo(fields);
    await dispatch('fetchCondos');
    return data;
  },
  async editCondo({ dispatch }, { id, fields }) {
    const { data } = await updateCondo(id, fields);
    await dispatch('fetchCondos');
    return data;
  },
  async archiveCondo({ dispatch }, { id, active }) {
    const { data } = await setCondoActive(id, active);
    await dispatch('fetchCondos');
    return data;
  },
  async removeCondo({ dispatch }, id) {
    await deleteCondo(id);
    await dispatch('fetchCondos');
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
